import { useEffect, useRef } from 'react'
import { createApp, type App as VueApp, type Component } from 'vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { pinia } from '@/core/store/pinia'
import vuetify from '@/core/plugins/vuetify'
import { queryClient } from '@/core/cache/queryClient'
import './vue-island.css'

export type VueIslandProps = {
  /** The Vue component to mount. Islands are opaque: replace them, never edit them. */
  component: Component
  /** Props for the component. Give it a stable identity — a change remounts the island. */
  props?: Record<string, unknown>
  /**
   * Runs after Pinia, Vuetify and VueQueryPlugin are installed and before mount:
   * the place for the router or the session bootstrap. Stable identity, as above.
   */
  setup?: (app: VueApp) => void
  className?: string
}

/**
 * Mounts a Vue component inside the React tree, with the shared plugins: the one
 * Pinia, the Vuetify instance, and VueQueryPlugin holding the SAME QueryClient
 * React's provider holds — so caches, invalidations and session state are one.
 * Unmounts the Vue app when the React component unmounts or its inputs change.
 */
export function VueIsland({ component, props, setup, className }: VueIslandProps) {
  const host = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = host.current
    if (!el) return
    const app = createApp(component, props ?? {})
      .use(pinia)
      .use(vuetify)
      .use(VueQueryPlugin, { queryClient })
    setup?.(app)
    app.mount(el)
    return () => {
      app.unmount()
    }
  }, [component, props, setup])

  return <div ref={host} className={className} data-vue-island="" />
}
